import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { colors } from '@src/styles';

export interface Organization {
  id: string;
  name: string;
}

interface OrganizationAutocompleteProps {
  organizations: Record<string, string>;
  isLoading: boolean;
  value: string;
  onChange: (organizationId: string | null) => void;
}

export const OrganizationAutocomplete: React.FC<OrganizationAutocompleteProps> = ({
  organizations,
  isLoading,
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  const organizationOptions: Organization[] = Object.entries(organizations)
    .map(([id, name]) => ({
      id,
      name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (value && organizations[value]) {
      setSelectedOrg({ id: value, name: organizations[value] });
    } else {
      setSelectedOrg(null);
    }
  }, [value, organizations]);

  const handleChange = (_event: React.SyntheticEvent, newValue: Organization | null) => {
    setSelectedOrg(newValue);
    onChange(newValue?.id || null);
  };

  const filterOptions = (options: Organization[], { inputValue }: { inputValue: string }) => {
    const searchValue = inputValue.toLowerCase().trim();

    if (!searchValue) {
      return options;
    }

    return options.filter((option) => {
      const nameLower = option.name.toLowerCase();
      const idLower = option.id.toLowerCase();
      return nameLower.includes(searchValue) || idLower.includes(searchValue);
    });
  };

  return (
    <Autocomplete
      options={organizationOptions}
      value={selectedOrg}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={(option) => option.name}
      loading={isLoading}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={filterOptions}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body1" sx={{ color: colors.primary.white }}>
              {option.name}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.background.secondary, fontSize: '0.75rem' }}>
              ID: {option.id}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Organização"
          variant="outlined"
          size="small"
          placeholder="Buscar por nome ou ID"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            minWidth: 250,
            '& .MuiInputLabel-root': {
              color: colors.primary.white,
              '&.Mui-focused': {
                color: colors.primary.yellow,
              },
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: colors.background.secondary,
              },
              '&:hover fieldset': {
                borderColor: colors.primary.yellow,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary.yellow,
              },
              '& input': {
                color: colors.primary.white,
              },
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: colors.primary.white,
            },
            '& .MuiAutocomplete-clearIndicator': {
              color: colors.primary.white,
            },
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-paper': {
          backgroundColor: colors.background.secondary,
        },
        '& .MuiAutocomplete-listbox': {
          backgroundColor: colors.background.secondary,
        },
        '& .MuiAutocomplete-option': {
          '&:hover': {
            backgroundColor: colors.background.primary,
          },
          '&[aria-selected="true"]': {
            backgroundColor: `${colors.primary.yellow}20 !important`,
          },
        },
      }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: colors.background.secondary,
            '& .MuiAutocomplete-noOptions': {
              color: colors.primary.white,
            },
          },
        },
      }}
      noOptionsText="Nenhuma organização encontrada"
    />
  );
};
